// Constants for AutoApproval components

import { AssignmentStatusEnum } from 'api/model';

/**
 * Maximum number of assignments per page for auto approval table.
 */
const MAX_APPROVAL_PAGE_SIZE = 50;

/**
 * Statusar där ett uppdrag får godkännas från efterkontrollen: Slutfört eller Avbokat.
 * Speglar backend-vakten i ApproveMultipleAssignmentsWithAutoApprovalConditions
 * (BulkApprovableStatuses) — styr både radernas checkboxar och välj alla-knapparna.
 */
export const APPROVABLE_ASSIGNMENT_STATUSES: AssignmentStatusEnum[] = [
  AssignmentStatusEnum.Complete,
  AssignmentStatusEnum.Canceled,
];

export const isApprovableStatus = (
  status: AssignmentStatusEnum | undefined
): boolean =>
  status !== undefined && APPROVABLE_ASSIGNMENT_STATUSES.includes(status);

const HOUR_MS = 60 * 60 * 1000;

/**
 * Kl 02 lokal tid på `day`s kalenderdag + `dayOffset` dagar — samma instant som backendens
 * nattgräns. Räknas som 03:00 minus en timme så att DST blir som i backend: på höstens dygn
 * (02:00 inträffar två gånger) blir det den senare förekomsten (normaltid), och på vårens dygn
 * (02:00 finns inte) blir det 03:00.
 */
const twoAmOn = (day: Date, dayOffset: number): Date => {
  const threeAm = new Date(day);
  threeAm.setDate(threeAm.getDate() + dayOffset);
  threeAm.setHours(3, 0, 0, 0);
  const twoAm = new Date(threeAm.getTime() - HOUR_MS);
  return twoAm.getHours() === 2 ? twoAm : threeAm;
};

/** Första nattgränsen (kl 02) efter `instant`. */
const nextBoundaryAfter = (instant: Date): Date => {
  const sameDay = twoAmOn(instant, 0);
  return instant < sameDay ? sameDay : twoAmOn(instant, 1);
};

/**
 * Manuellt godkännande är spärrat tills det nattliga autogodkännandet haft sin chans —
 * samma gräns som auto-eligibility: kl 02 dagen efter uppdragets STARTdag, så att ett
 * uppdrag som gått över midnatt hör till startdagens nattbatch. Slutfördes uppdraget först
 * efter den gränsen (flerdygnsuppdrag, nattkörning förbi kl 02) gäller i stället nästa kl 02
 * efter sluttiden. Startdagen tas från den tidigaste av start- och sluttid; saknas starttid
 * räknas sluttidens dag. Måste matcha AutoApproveGracePeriod.GetEligibleAt i backend
 * (svensk tid; admin-klienterna kör i svensk tid så lokal tid är samma sak).
 */
export const getManualApprovalUnlockTime = (
  startTime: Date | null | undefined,
  endTime: Date | null | undefined
): Date | null => {
  if (!endTime) return null;
  const end = new Date(endTime);
  const start = startTime ? new Date(startTime) : null;
  const dayAnchor = start && start < end ? start : end;
  const startDayBoundary = twoAmOn(dayAnchor, 1);
  const nextAfterEnd = nextBoundaryAfter(end);
  return startDayBoundary > nextAfterEnd ? startDayBoundary : nextAfterEnd;
};

/**
 * True när uppdraget får godkännas manuellt: nattgränsen efter startdagen (se
 * getManualApprovalUnlockTime) har passerat, eller sluttid saknas (då kan backend-spärren
 * ändå inte räkna ut en gräns).
 *
 * Undantag — extern lastbilstransportör: uppdrag körda av en FT med flaggan
 * `isTruckTransport` ("Extern lastbilstransportör") får godkännas manuellt direkt efter
 * slutförande, utan att invänta natten. Speglar backend-undantaget i
 * CaseLogic.UpdateCaseAssignment (manuell godkännandespärr).
 */
export const isManualApprovalUnlocked = (
  startTime: Date | null | undefined,
  endTime: Date | null | undefined,
  isExternalTruckTransporter?: boolean | null,
  now: Date = new Date()
): boolean => {
  if (isExternalTruckTransporter) return true;
  const unlockAt = getManualApprovalUnlockTime(startTime, endTime);
  return unlockAt === null || unlockAt <= now;
};

export default MAX_APPROVAL_PAGE_SIZE;
